import { posthog } from 'posthog-js';
import { PostHogProvider as PostHogProviderFromPosthog } from 'posthog-js/react';
import type React from 'react';
import type { PropsWithChildren } from 'react';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { APP_ENV, POSTHOG_HOST, POSTHOG_KEY } from '../../lib/constants.ts';
import { classifyDevice } from '../../lib/device-class.ts';
import { i18n } from '../../lib/i18n/i18n.ts';
import { toSupportedLanguage } from '../../lib/i18n/language-config.ts';
import { maskTapiPhoneUrl } from '../../lib/mask-tapi-phone-url.ts';

export const PosthogProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const init = useRef(false);
    const location = useLocation();

    useEffect(() => {
        if (!POSTHOG_KEY) {
            return;
        }
        if (typeof window === 'undefined') {
            return;
        }
        if (process.env.NODE_ENV === 'development') {
            return;
        }
        if (init.current) {
            return;
        }
        init.current = true;

        posthog.init(POSTHOG_KEY, {
            api_host: POSTHOG_HOST || 'https://app.posthog.com',
            loaded: (posthog) => {
                if (process.env.NODE_ENV === 'development') {
                    posthog.debug();
                }
            },
            // Strip the caller's phone number out of URL properties before any
            // event leaves the browser — the TAPI route carries it in the path.
            sanitize_properties: (properties) => {
                const urlKeys = [
                    '$current_url',
                    '$pathname',
                    '$referrer',
                    '$initial_current_url',
                    '$initial_pathname',
                    '$initial_referrer',
                ] as const;
                for (const key of urlKeys) {
                    const value: unknown = properties[key];
                    if (typeof value === 'string') {
                        properties[key] = maskTapiPhoneUrl(value);
                    }
                }
                return properties;
            },
            // The pathname effect below is the single source of $pageview.
            // The SDK's own initial pageview would double-count hard loads —
            // and on background-opened tabs (TAPI) it fires minutes late, on
            // focus.
            capture_pageview: false,
            // Defaults to 'if_capture_pageview', which the line above would
            // silently turn off.
            capture_pageleave: true,
            // Must stay a boolean: posthog-js only lets the client config win when
            // it is one, and falls back to the project-level toggle otherwise.
            capture_dead_clicks: true,
            session_recording: {
                maskAllInputs: false,
                maskInputOptions: {
                    password: true,
                },
            },
        });

        const touch =
            'ontouchstart' in window ||
            navigator.maxTouchPoints > 0 ||
            (navigator as unknown as { msMaxTouchPoints: number }).msMaxTouchPoints > 0;

        posthog.register({
            touch,
            // Hardware-based device class ($device_type files iPads under
            // Desktop because iPadOS Safari sends a desktop user agent).
            device_class: classifyDevice(window.screen.width, window.screen.height, touch),
            app_language: toSupportedLanguage(i18n.language),
        });

        i18n.on('languageChanged', (lng) => {
            posthog.register({ app_language: toSupportedLanguage(lng) });
        });

        posthog.capture('$set', {
            $set_once: { first_seen_date: new Date().toISOString() },
        });
    }, []);

    // Single source of $pageview: fires on mount and on every SPA navigation.
    // This must live below the init effect (same-component effects run in
    // declaration order, and posthog.init is synchronous) — a capture placed
    // above the AuthGuard runs before init on hard loads and posthog-js
    // silently drops pre-init captures instead of queueing them.
    useEffect(() => {
        if (APP_ENV === 'production') {
            posthog.capture('$pageview');
        }
    }, [location.pathname]);

    return <PostHogProviderFromPosthog client={posthog}>{children}</PostHogProviderFromPosthog>;
};
